import React from 'react';
import {useAuth} from '../../context/AuthContext';
import {listIncludedApps, type IncludedApp} from '../../api/includedAppsApi';

export const DEFAULT_ICON = '/favicon.svg';
export function safeIconPath(value?: string | null): string {
  if (!value || value.length>512 || !/^\/[A-Za-z0-9_./-]+\.(?:svg|png|ico|webp|jpe?g)$/i.test(value) || value.startsWith('//') || value.startsWith('/api/')) return DEFAULT_ICON;
  try {const parsed=new URL(value,window.location.origin);return parsed.origin===window.location.origin&&parsed.pathname===value?value:DEFAULT_ICON;} catch {return DEFAULT_ICON;}
}
export function browserTitle(name?: string): string {return name&&name!=='Conductor'?name+' · Conductor':'Conductor';}
export function matchingIncludedApp(apps: IncludedApp[], pathname: string): IncludedApp | undefined {
  return apps.filter(app=>{
    const route=new URL(app.launchPath,window.location.origin).pathname.replace(/\/$/,'');
    return pathname===route||pathname.startsWith(route+'/');
  }).sort((a,b)=>new URL(b.launchPath,window.location.origin).pathname.length-new URL(a.launchPath,window.location.origin).pathname.length)[0];
}

type Identity={owner:symbol;scope:string;name?:string;iconPath?:string|null};
type Controls={set:(owner:symbol,name?:string,iconPath?:string|null)=>void;clear:(owner:symbol)=>void};
const Context=React.createContext<Controls|null>(null);

export function BrowserIdentityProvider({children}:{children:React.ReactNode}):React.ReactElement {
  const {user,loading,setupRequired}=useAuth();
  const scope=user?.id||'anonymous',isSignedIn=!!user;
  const [pathname,setPathname]=React.useState(window.location.pathname);
  const [view,setView]=React.useState<Identity|null>(null);
  const [installed,setInstalled]=React.useState<{scope:string;pathname:string;app?:IncludedApp}|null>(null);
  React.useEffect(()=>{
    const changed=()=>setPathname(window.location.pathname);
    window.addEventListener('popstate',changed);
    return()=>window.removeEventListener('popstate',changed);
  },[]);
  React.useEffect(()=>{
    let active=true;
    if(!isSignedIn||pathname==='/'||pathname.startsWith('/apps/')){setInstalled(null);return;}
    listIncludedApps().then(apps=>{if(active)setInstalled({scope,pathname,app:matchingIncludedApp(apps,pathname)});})
      .catch(()=>{if(active)setInstalled(null);});
    return()=>{active=false;};
  },[scope,user?.role,pathname,isSignedIn]);
  const set=React.useCallback((owner:symbol,name?:string,iconPath?:string|null)=>setView({owner,scope,name,iconPath}),[scope]);
  const clear=React.useCallback((owner:symbol)=>setView(current=>current?.owner===owner?null:current),[]);
  const controls=React.useMemo(()=>({set,clear}),[set,clear]);
  const currentView=view?.scope===scope?view:null;
  const app=!loading&&user&&installed?.scope===scope&&installed.pathname===pathname?installed.app:undefined;
  const publicRoute=pathname.startsWith('/apps/');
  const name=loading?undefined:setupRequired?'Set up':!user&&!publicRoute?'Sign in':app?.name??currentView?.name;
  const iconPath=safeIconPath(!loading&&(user||publicRoute)?app?.iconPath??currentView?.iconPath:undefined);
  React.useLayoutEffect(()=>{
    document.title=browserTitle(name);
    let link=document.querySelector<HTMLLinkElement>('#conductor-favicon');
    if(!link){link=document.createElement('link');link.id='conductor-favicon';link.rel='icon';document.head.appendChild(link);}
    const favicon=link;
    favicon.href=DEFAULT_ICON;favicon.type='image/svg+xml';favicon.setAttribute('sizes','any');
    if(iconPath===DEFAULT_ICON)return;
    let active=true;
    const candidate=new Image();
    const timeout=window.setTimeout(()=>{active=false;},5000);
    candidate.onload=()=>{
      if(!active||!candidate.naturalWidth)return;
      favicon.href=iconPath;
      favicon.type=iconPath.toLowerCase().endsWith('.svg')?'image/svg+xml':iconPath.toLowerCase().endsWith('.ico')?'image/x-icon':iconPath.toLowerCase().endsWith('.webp')?'image/webp':/\.jpe?g$/i.test(iconPath)?'image/jpeg':'image/png';
      favicon.removeAttribute('sizes');window.clearTimeout(timeout);
    };
    candidate.onerror=()=>window.clearTimeout(timeout);
    candidate.src=iconPath;
    return()=>{active=false;window.clearTimeout(timeout);candidate.onload=null;candidate.onerror=null;};
  },[name,iconPath]);
  return <Context.Provider value={controls}>{children}</Context.Provider>;
}

export function usePageIdentity(name?:string,iconPath?:string|null):void {
  const controls=React.useContext(Context),owner=React.useRef(Symbol('page-identity'));
  React.useLayoutEffect(()=>{
    if(!controls)return;
    controls.set(owner.current,name,iconPath);
    const id=owner.current;
    return()=>controls.clear(id);
  },[controls,name,iconPath]);
}
